import { ReparoApi } from './index';

/**
 * Sales Orders read (docs/prds/sales-orders-v1.md): purchase orders that marketplaces and B2B
 * partners raised on the brand. The scope (OUTGOING + origin PARTNER) is fixed server-side;
 * these calls only carry filters. Backed by /b2b-po/sales-orders/* in the po-fulfillment module.
 */

export type SalesOrderStatus =
    | 'OPEN'
    | 'PARTIALLY_DISPATCHED'
    | 'DISPATCHED'
    | 'FULFILLED'
    | 'LAPSED'
    | 'EXPIRED'
    | 'CANCELLED';

export type SalesOrderLens =
    | 'open'
    | 'partially_dispatched'
    | 'dispatched'
    | 'fulfilled'
    | 'lapsed'
    | 'short'
    | 'cancelled_or_expired';

export type SalesOrderLineStatus = 'UNFULFILLED' | 'PARTIALLY_FULFILLED' | 'FULFILLED' | 'CANCELLED';

export interface SalesOrderListRequest {
    channels?: string[];
    lens?: SalesOrderLens;
    startDate?: string;
    endDate?: string;
    search?: string;
    page?: number;
    pageSize?: number;
    fetchAll?: boolean;
}

export interface SalesOrderListItem {
    purchaseOrderId: string;
    channel?: string;
    warehouseId?: string;
    destinationName?: string;
    itemsCount: number;
    totalQuantity: number;
    actualQuantity?: number;
    orderDate: string;
    expiryDate?: string;
    status: string;
    derivedStatus?: SalesOrderStatus;
    dispatchDate?: string;
    poRefNum?: string;
    origin?: 'PARTNER' | 'BRAND';
    partnerReceivedQuantity?: number;
    /** Order value before tax, summed over the order's lines; null when no line is priced. */
    orderValue?: number | null;
    orderValueWithTax?: number | null;
    /** Under itemsCount means orderValue is a partial total. */
    pricedItemsCount?: number;
    returnedUnits?: number;
    returnedValue?: number;
}

export interface SalesOrderListResponse {
    data: SalesOrderListItem[];
    total: number;
    page: number;
    pageSize: number;
    hasNext: boolean;
    unclassifiedCount: number;
}

export interface SalesOrderLine {
    purchaseOrderId: string;
    channel?: string;
    orderDate: string;
    expiryDate?: string;
    status: string;
    productId: string;
    name?: string;
    sku?: string;
    orderedQty: number;
    dispatchedQty: number;
    partnerReceivedQty?: number | null;
    shortQty: number;
    lineStatus: SalesOrderLineStatus;
    returnedUnits?: number;
}

export interface SalesOrderLinesResponse {
    data: SalesOrderLine[];
    total: number;
    page: number;
    pageSize: number;
    hasNext: boolean;
}

export interface SalesOrderSummary {
    openOrders: number;
    expiringIn48h: number;
    partiallyDispatched: number;
    /** Order value before tax of the orders each count covers - whole orders, not the unshipped part. */
    openValue: number;
    expiringIn48hValue: number;
    partiallyDispatchedValue: number;
    fillRate30d: number | null;
    byStatus: Record<SalesOrderStatus, number>;
    shortOrders: number;
    unclassifiedCount: number;
}

export interface SalesOrderDispatchLine {
    productId: string;
    name?: string;
    sku?: string;
    quantity: number;
}

export interface SalesOrderDispatch {
    code: string | null;
    date: string;
    warehouseId: string | null;
    units: number;
    lines: SalesOrderDispatchLine[];
}

export interface SalesOrderReturn {
    id: string;
    productId: string | null;
    quantity: number;
    value: number | null;
    date: string | null;
    reason: string | null;
    disposition: string | null;
    reference: string | null;
}

export interface SalesOrderTracker {
    status: string;
    actByAt: string;
    isBreaching: boolean;
    actedAt: string | null;
    expectedDispatchAt: string | null;
    dispatchedAt: string | null;
    planId: string | null;
    saleOrderCode: string | null;
}

export interface SalesOrderPlan {
    planId: string;
    planStatus: string;
    approvedAt: string | null;
    approvedQty: number;
}

export interface SalesOrderDetailLine {
    productId: string;
    name?: string;
    sku?: string;
    quantity: number;
    dispatchedQuantity: number;
    partnerReceivedQuantity?: number | null;
    /** As stated on the order: the first of each pair is before tax, the second after. null = not stated. */
    unitPrice?: number | null;
    landingRate?: number | null;
    lineTotal?: number | null;
    lineTotalWithTax?: number | null;
    /** GST slab in percent. */
    gstRate?: number | null;
}

export interface SalesOrderDetail {
    purchaseOrderId: string;
    channel?: string;
    origin: 'PARTNER' | 'BRAND';
    status: string;
    derivedStatus: SalesOrderStatus;
    orderDate: string;
    receivedAt: string;
    expiryDate?: string;
    dispatchDate?: string;
    poRefNum?: string;
    destinationWarehouseId?: string;
    destinationName?: string;
    orderedQty: number;
    dispatchedQty: number;
    returnedUnits: number;
    returnedValue: number;
    lastSyncedAt: string;
    lines: SalesOrderDetailLine[];
    dispatches: SalesOrderDispatch[];
    returns: SalesOrderReturn[];
    tracker: SalesOrderTracker | null;
    plan: SalesOrderPlan | null;
    history: Array<{ status: string; at: string }>;
}

export const listSalesOrders = async (body: SalesOrderListRequest): Promise<SalesOrderListResponse> =>
    ReparoApi<SalesOrderListResponse>({
        method: 'POST',
        url: '/b2b-po/sales-orders/list',
        body: JSON.stringify(body),
    });

export const listSalesOrderLines = async (body: SalesOrderListRequest): Promise<SalesOrderLinesResponse> =>
    ReparoApi<SalesOrderLinesResponse>({
        method: 'POST',
        url: '/b2b-po/sales-orders/lines',
        body: JSON.stringify(body),
    });

export const getSalesOrderSummary = async (body: Pick<SalesOrderListRequest, 'channels'>): Promise<SalesOrderSummary> =>
    ReparoApi<SalesOrderSummary>({
        method: 'POST',
        url: '/b2b-po/sales-orders/summary',
        body: JSON.stringify(body),
    });

export const getSalesOrder = async (purchaseOrderId: string): Promise<SalesOrderDetail> =>
    ReparoApi<SalesOrderDetail>({
        method: 'GET',
        url: `/b2b-po/sales-orders/${encodeURIComponent(purchaseOrderId)}`,
        body: null,
    });
